/*
  Old Leza "Shop" mega menu behaviour on top of Dawn's <details> + header-menu (click, touch and keyboard
  stay Dawn's accessible disclosure; Escape closes).
  - Mouse: opens on hover immediately, closes 320ms after the pointer leaves the navigation, so the
    pointer can travel from "Shop" down into the panel. One delegated listener pair, no scroll/resize work.
  - A click on "Shop" while its panel was opened by hover goes to the item's link (old nav link).
  - Card images in the closed panels are lazy and not rendered; they are switched to eager the first time
    the pointer or focus enters the navigation, so they are ready when a panel opens without ever
    competing with the page's first view.
*/
(() => {
  const nav = document.querySelector('.header__inline-menu');
  if (!nav) return;

  let warmed = false;
  const warm = () => {
    if (warmed) return;
    warmed = true;
    nav.querySelectorAll('.mega-menu__content img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });
  };
  nav.addEventListener('pointerenter', warm, { once: true });
  nav.addEventListener('focusin', warm, { once: true });

  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  // Desktop pointers can open the panel at any moment: once the page itself has loaded and gone idle,
  // fetch the few card images so a slow connection never shows empty cards (phones never download them).
  if (window.matchMedia('(min-width: 990px)').matches) {
    const idleWarm = () => ('requestIdleCallback' in window ? requestIdleCallback(warm, { timeout: 3000 }) : setTimeout(warm, 1500));
    if (document.readyState === 'complete') idleWarm();
    else window.addEventListener('load', idleWarm, { once: true });
  }

  const CLOSE_DELAY = 320;
  let closeTimer;
  let openedByHover = null;

  const setOpen = (details, open) => {
    if (details.open === open) return;
    details.open = open;
    details.querySelector('summary')?.setAttribute('aria-expanded', open);
  };

  const closeAll = (except) => {
    nav.querySelectorAll('details.mega-menu[open]').forEach((details) => details !== except && setOpen(details, false));
    if (!except) openedByHover = null;
  };

  nav.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse') return;
    clearTimeout(closeTimer);
    const details = event.target.closest('details.mega-menu');
    if (!details) {
      // Pointer is on another top-level item: close any open panel after the grace period.
      if (!event.target.closest('.mega-menu__content')) closeTimer = setTimeout(closeAll, CLOSE_DELAY);
      return;
    }
    if (details.open) return;
    closeAll(details);
    setOpen(details, true);
    openedByHover = details;
  });

  nav.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'mouse' || nav.contains(event.relatedTarget)) return;
    clearTimeout(closeTimer);
    closeTimer = setTimeout(closeAll, CLOSE_DELAY);
  });

  nav.addEventListener('click', (event) => {
    const summary = event.target.closest('summary[data-leza-href]');
    if (!summary || event.detail === 0) return; // keyboard activation keeps the disclosure
    if (openedByHover === summary.parentElement) {
      event.preventDefault();
      window.location.href = summary.dataset.lezaHref;
    }
  });
})();

/*
  Mobile drawer (live Leza drawer): the backdrop layer sits on <header-drawer> (see leza-header.css), so a tap
  on it closes the drawer through Dawn's own toggle; while Dawn closes the panel, "leza-drawer-closing" lets
  the round close button slide out with it. Dawn's focus trap / Escape / focus return are untouched.
*/
(() => {
  document.querySelectorAll('header-drawer').forEach((drawer) => {
    const details = drawer.querySelector('details');
    const toggle = details && details.querySelector('summary');
    if (!toggle) return;
    drawer.addEventListener('click', (event) => {
      if (event.target === drawer && details.classList.contains('menu-opening')) toggle.click();
    });
    // Closing = Dawn removed "menu-opening" while the details are still open (not the brief moment before
    // "menu-opening" is first added on open).
    // classList.toggle is only called on a real change: writing the class attribute re-triggers the observer.
    let wasOpen = false;
    const setClosing = (on) => {
      if (details.classList.contains('leza-drawer-closing') !== on) details.classList.toggle('leza-drawer-closing', on);
    };
    new MutationObserver(() => {
      if (details.classList.contains('menu-opening')) {
        wasOpen = true;
        setClosing(false);
      } else if (!details.open) {
        wasOpen = false;
        setClosing(false);
      } else if (wasOpen) {
        setClosing(true);
      }
    }).observe(details, { attributes: true, attributeFilter: ['class', 'open'] });
  });
})();

/*
  Old cart-count bump: Dawn re-renders #cart-icon-bubble after every cart change, so a newly inserted bubble
  gets the 0.125s grow (CSS .is-bumping). Nothing runs on page load; reduced motion is handled in CSS.
*/
(() => {
  const icon = document.getElementById('cart-icon-bubble');
  if (!icon || !window.MutationObserver) return;
  new MutationObserver(() => {
    const bubble = icon.querySelector('.cart-count-bubble');
    if (!bubble || bubble.classList.contains('is-bumping')) return;
    bubble.classList.add('is-bumping');
    bubble.addEventListener('animationend', () => bubble.classList.remove('is-bumping'), { once: true });
  }).observe(icon, { childList: true });
})();
